import {
  TPermissionModel,
  TPolicy,
} from '@/shared/features/permissions/utils/build-permission-model.types';

const toArray = <T>(value: T | T[]): T[] =>
  Array.isArray(value) ? value : [value];

// Action strings are fully prefixed: `entity:actionName` (e.g. 'backofficeTenant:readTenant').
const splitAction = (action: string): { entity: string; name: string } => {
  const [entity, name = ''] = action.split(':');

  return { entity, name };
};

const isReadAction = (name: string): boolean =>
  name.toLowerCase().startsWith('read');

const actionMatches = (
  granted: string,
  entity: string,
  action: string,
): boolean =>
  granted === '*' || granted === `${entity}:*` || granted === action;

const resourceAllows = (
  resource: string,
  entity: string,
  id: string,
  model: TPermissionModel,
): boolean => {
  if (resource === '*' || resource === `${entity}:*`) {
    return true;
  }

  const [, aggregationType, resourceId] = resource.split(':');

  if (!aggregationType || !resourceId) {
    return false;
  }

  if (aggregationType === 'id') {
    return resourceId === id;
  }

  if (aggregationType === 'group') {
    const groupId = model.idToGroupId.get(entity)?.get(id);

    return resourceId === groupId || model.isChildOf(resourceId, groupId);
  }

  return false;
};

const policyGrants = (
  policy: TPolicy,
  entity: string,
  action: string,
  id: string | undefined,
  model: TPermissionModel,
): boolean => {
  if (!policy.action.some((a) => actionMatches(a, entity, action))) {
    return false;
  }

  // No-id actions (e.g. createTenant): presence of the action grant is enough.
  if (id === undefined) {
    return true;
  }

  return policy.resource.some((r) => resourceAllows(r, entity, id, model));
};

const canSingle = (
  model: TPermissionModel,
  action: string,
  id: string | undefined,
): boolean => {
  const { entity, name } = splitAction(action);

  // Read short-circuit: the backend returned only readable items, so the
  // prefetched readable-id set is the authoritative read authorization.
  if (id !== undefined && isReadAction(name) && model.readableIds.has(entity)) {
    return Boolean(model.readableIds.get(entity)?.has(id));
  }

  return model.policies.some((policy) =>
    policyGrants(policy, entity, action, id, model),
  );
};

const normalizeIds = (ids?: string | string[]): (string | undefined)[] =>
  ids === undefined ? [undefined] : toArray(ids);

// Every action permitted on every id.
export const can = (
  model: TPermissionModel,
  actions: string | string[],
  ids?: string | string[],
): boolean => {
  const idList = normalizeIds(ids);

  return toArray(actions).every((action) =>
    idList.every((id) => canSingle(model, action, id)),
  );
};

// At least one action/id combination permitted.
export const canAny = (
  model: TPermissionModel,
  actions: string | string[],
  ids?: string | string[],
): boolean => {
  const idList = normalizeIds(ids);

  return toArray(actions).some((action) =>
    idList.some((id) => canSingle(model, action, id)),
  );
};
